<script setup lang="ts">
import { onMounted, onServerPrefetch } from 'vue'
import { useRoute } from 'vue-router'
import CategoryPage from '@/pages/Category.vue'
import MarketingPageComponent from '@/pages/Marketing.vue'
import Marketplace from '@/pages/marketplace/Marketplace.vue'
import { useEstablishmentHttp } from '@/http/establishmentHttp'
import { useCategoriesHttp } from '@/http/categoriesHttp'
import { useHttp } from '@/composables/useHttp'
import { getCountry } from '@/router/utils/getCountry'
import CategoryResource from '@/resources/CategoryResource'
import { MarketplaceEstablishment } from '@/http/models/MarketplaceEstablishment'
import { City } from '@/http/models/City'
import { MarketingPage } from '@/http/models/MarketingPage'
import { useSafeSSRContext } from '@/utils/useSafeSSRContext'
import { useMarketingPageStore } from '@/stores/ssr/useMarketingPageStore'
import { storeToRefs } from 'pinia'
import { SlugType } from '@/enums/SlugType'

const {
  page,
  city,
  marketingPage,
  marketingRelated,
  categoryEstablishments,
  categoryCategories,
  categoryCategory,
} = storeToRefs(useMarketingPageStore())

const route = useRoute()
const http = useHttp()
const {establishmentsByCategory, establishmentsByCityAndCountry} = useEstablishmentHttp()
const categoriesHttp = useCategoriesHttp()

const getSlugInformation = async (slug: string) => {
  const {data: {data: localeSlugs}} = await http.get(`/countries/${getCountry().value}/slugs/${slug}`)
  return localeSlugs
}

const initMarketingPage = async (slugInformation) => {
  const marketingPageHolder = slugInformation

  if (marketingPageHolder.relationships.establishments.length === 0) {
    const categories = marketingPageHolder.relationships.category !== null ? [marketingPageHolder.relationships.category.id] : []

    marketingPageHolder.relationships.establishments = await establishmentsByCityAndCountry(marketingPageHolder.relationships.city.attributes.slug, getCountry().value, categories)
  }

  const {data: relatedData} = await http.get(`marketing-pages/${route.params.slug}/related`)

  marketingPage.value = new MarketingPage(marketingPageHolder)

  marketingRelated.value = relatedData
}

async function initCategoryPage() {
  const [establishments, categoryList, category] = await Promise.all([
    establishmentsByCategory(route.params.slug),
    categoriesHttp.categories(),
    categoriesHttp.category(route.params.slug),
  ])

  categoryEstablishments.value = establishments.map(establishment => new MarketplaceEstablishment(establishment))
  categoryCategories.value = categoryList
  categoryCategory.value = new CategoryResource(category)
}

const initSlugPage = async () => {
  const result = await getSlugInformation(route.params.slug)

  page.value = result.type

  if (result.type === SlugType.MarketingPages) {
    await initMarketingPage(result)
  }

  if (result.type === SlugType.Categories) {
    await initCategoryPage()
  }

  if (result.type === SlugType.Cities) {
    city.value = new City(result)
  }
}

onMounted(async () => {
  await initSlugPage()
})

onServerPrefetch(async () => {
  const {redirect} = useSafeSSRContext()

  try {
    await initSlugPage()
  } catch (e) {
    console.log(e)
    redirect('/')
  }
})
</script>

<template>
  <template v-if="page === SlugType.Categories && categoryEstablishments && categoryCategories && categoryCategory">
    <CategoryPage
        :establishments="categoryEstablishments"
        :categories="categoryCategories"
        :category="categoryCategory"
    />
  </template>
  <template v-if="page === SlugType.MarketingPages && marketingPage">
    <MarketingPageComponent
        :page="marketingPage"
        :related="marketingRelated"
    />
  </template>
  <template v-if="page === SlugType.Cities && city">
    <Marketplace
        :city="city"
    />
  </template>
</template>
